import React, { useState } from 'react';
import { useTheme } from '../../theme';
import { Button } from '../ui';
import { toast } from 'react-toast';
import { useSidebarStateContext } from '../shell/SidebarStateContext';

const AppearanceSettings = () => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const { locked, toggleLock } = useSidebarStateContext();
  const [compactMode, setCompactMode] = useState(false);
  const [animationsEnabled, setAnimationsEnabled] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Appearance preferences saved');
  };

  const themes = [
    {
      id: 'light',
      label: 'Light',
      preview: {
        bg: '#F7F9FC',
        surface: '#FFFFFF',
        sidebar: '#FFFFFF',
        border: '#E2E8F0',
      },
    },
    {
      id: 'dark',
      label: 'Dark',
      preview: {
        bg: '#07111F',
        surface: '#0D1A2B',
        sidebar: '#0B1628',
        border: '#1D3149',
      },
    },
    {
      id: 'system',
      label: 'System',
      preview: {
        bg: 'linear-gradient(135deg, #F7F9FC 50%, #07111F 50%)',
        surface: '#FFFFFF',
        sidebar: '#0B1628',
        border: '#94A3B8',
      },
    },
  ];

  return (
    <div className="settings-section">
      <div className="settings-section-intro">
        <h2 className="settings-section-title">Appearance</h2>
        <p className="settings-subtitle">Theme and display preferences for Robitest.</p>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 24, fontSize: 14 }}>
        Choose Cloudlight (light) or Midnight Control Center (dark). Preference is saved on this device.
        Current resolved theme: <strong style={{ color: 'var(--text-primary)' }}>{resolvedTheme}</strong>
      </p>

      <form onSubmit={handleSubmit}>
        <div className="settings-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <h3 className="settings-card-title" style={{ color: 'var(--text-primary)' }}>Theme mode</h3>
          <p className="settings-card-description" style={{ color: 'var(--text-secondary)' }}>
            Default is System — follows your OS preference.
          </p>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16 }}>
            {themes.map((opt) => {
              const selected = theme === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setTheme(opt.id)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                  }}
                >
                  <div
                    style={{
                      width: 128,
                      height: 84,
                      background: opt.preview.bg,
                      border: selected ? '2px solid var(--primary)' : '2px solid var(--border)',
                      borderRadius: 8,
                      overflow: 'hidden',
                      boxShadow: selected ? 'var(--shadow-sm)' : 'none',
                    }}
                  >
                    <div style={{ height: 18, background: opt.preview.sidebar, borderBottom: `1px solid ${opt.preview.border}` }} />
                    <div style={{ flex: 1, padding: 6, display: 'flex', gap: 4, height: 'calc(100% - 18px)' }}>
                      <div style={{ width: 22, background: opt.preview.sidebar, borderRadius: 4, border: `1px solid ${opt.preview.border}` }} />
                      <div style={{ flex: 1, background: opt.preview.surface, borderRadius: 4, border: `1px solid ${opt.preview.border}` }} />
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: selected ? 600 : 400,
                      color: selected ? 'var(--primary)' : 'var(--text-secondary)',
                    }}
                  >
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="settings-card"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginTop: 16 }}
        >
          <h3 className="settings-card-title" style={{ color: 'var(--text-primary)' }}>Sidebar</h3>
          <p className="settings-card-description" style={{ color: 'var(--text-secondary)' }}>
            Expand on hover when collapsed. Use the sidebar panel button to keep it open.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={locked} onChange={toggleLock} />
            Keep sidebar expanded
          </label>
        </div>

        <div
          className="settings-card"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginTop: 16 }}
        >
          <h3 className="settings-card-title" style={{ color: 'var(--text-primary)' }}>Interface</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={compactMode} onChange={(e) => setCompactMode(e.target.checked)} />
            Compact density
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={animationsEnabled} onChange={(e) => setAnimationsEnabled(e.target.checked)} />
            Enable micro-interactions
          </label>
        </div>

        <div style={{ marginTop: 20 }}>
          <Button type="submit" variant="primary">
            Save preferences
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AppearanceSettings;
